<template>
  <div class="google-map-picker" :class="{ 'fill-height-mode': height === '100%' }">
    <v-row v-if="showSearch">
      <v-col cols="12">
        <v-text-field
          :id="resolvedAutocompleteId"
          v-model="searchQuery"
          prepend-inner-icon="mdi-magnify"
          :placeholder="placeholder"
          outlined
          dense
          hide-details="auto"
          class="mb-3"
        ></v-text-field>
      </v-col>
    </v-row>

    <div ref="mapContainer" :style="{ height: height === '100%' ? 'auto' : height }" class="map-container" :class="{ 'mb-3': height !== '100%' }"></div>

    <div v-if="showCoords" class="d-flex align-center justify-space-between coords-bar pa-2">
      <div class="d-flex align-center">
        <v-icon small color="grey" class="mr-1">mdi-crosshairs-gps</v-icon>
        <span class="text-caption grey--text text--darken-1">
          {{ lat.toFixed(6) }}, {{ lng.toFixed(6) }}
        </span>
      </div>
      <v-btn v-if="showReset" x-small text color="primary" @click="resetToInitial">
        รีเซ็ตตำแหน่ง
      </v-btn>
    </div>
  </div>
</template>

<script>
/* global google */

import { loadGoogleMapsScript } from "@/utils/googleMapsLoader";

function uid(prefix) {
  return `${prefix}-${Math.random().toString(36).slice(2, 11)}`;
}

export default {
  name: "GoogleMapPicker",
  props: {
    value: {
      type: Object,
      default: () => ({ lat: 13.7563, lng: 100.5018 }),
    },
    height: { type: String, default: "400px" },
    zoom: { type: Number, default: 15 },
    showSearch: { type: Boolean, default: true },
    showCoords: { type: Boolean, default: true },
    showReset: { type: Boolean, default: true },
    placeholder: { type: String, default: "ค้นหาสถานที่..." },
    /** ถ้าไม่ส่ง จะสร้าง id ไม่ซ้ำต่อ instance (กันหลายแผนที่ในหน้าเดียว) */
    autocompleteId: { type: String, default: "" },
    /**
     * true = emit address-resolved ให้ฟอร์มที่อยู่ sync จาก Google
     * false (ค่าเริ่ม) = แผนที่กับฟอร์มแยกกัน — อัปเดตแค่พิกัด + ช่องค้นหาบนแผนที่
     */
    syncAddressFields: { type: Boolean, default: false },
  },
  data() {
    return {
      internalAutocompleteId: "",
      map: null,
      marker: null,
      autocomplete: null,
      geocoder: null,
      lat: this.value?.lat || 13.7563,
      lng: this.value?.lng || 100.5018,
      searchQuery: "",
      isInitialSet: false,
    };
  },
  computed: {
    resolvedAutocompleteId() {
      return this.autocompleteId || this.internalAutocompleteId || "map-autocomplete";
    },
  },
  created() {
    this.internalAutocompleteId = uid("map-autocomplete");
  },
  mounted() {
    this.initMap();
  },
  methods: {
    async initMap() {
      try {
        await loadGoogleMapsScript();
        this.setupMap();
      } catch (e) {
        console.warn(e);
      }
    },
    setupMap() {
      const center = { lat: this.lat, lng: this.lng };
      this.map = new google.maps.Map(this.$refs.mapContainer, {
        center,
        zoom: this.zoom,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });

      this.marker = new google.maps.Marker({
        position: center,
        map: this.map,
        draggable: true,
      });

      this.geocoder = new google.maps.Geocoder();

      if (this.showSearch) {
        this.$nextTick(() => {
          const input = document.getElementById(this.resolvedAutocompleteId);
          if (!input) return;
          this.autocomplete = new google.maps.places.Autocomplete(input, {
            fields: ["address_components", "formatted_address", "geometry", "name"],
            componentRestrictions: { country: "th" },
          });
          this.autocomplete.bindTo("bounds", this.map);
          this.autocomplete.addListener("place_changed", this.onPlaceChanged);
        });
      }

      this.map.addListener("click", (e) => this.updatePosition(e.latLng.lat(), e.latLng.lng(), false));
      this.marker.addListener("dragend", (e) => this.updatePosition(e.latLng.lat(), e.latLng.lng(), false));
    },
    onPlaceChanged() {
      if (!this.autocomplete) return;
      const place = this.autocomplete.getPlace();
      if (!place.geometry || !place.geometry.location) return;

      if (place.geometry.viewport) {
        this.map.fitBounds(place.geometry.viewport);
      } else {
        this.map.setCenter(place.geometry.location);
        this.map.setZoom(17);
      }
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      this.updatePosition(lat, lng, true, place);
    },

    pickComponent(components, ...types) {
      const c = components.find((comp) => types.some((t) => comp.types.includes(t)));
      return c ? c.long_name : "";
    },

    /**
     * long_name จาก component ที่มี type ตรงกับ Places / Geocoder
     * (เช่น PlaceService.GetPlaceDetails, Autocomplete.getPlace, Geocoder)
     */
    pickByType(components, type) {
      if (!components?.length || !type) return "";
      const c = components.find((comp) => Array.isArray(comp.types) && comp.types.includes(type));
      return c && c.long_name != null ? String(c.long_name) : "";
    },

    /** จังหวัดจาก Google (ก่อน normalize) */
    pickRawProvince(components) {
      return this.pickByType(components, "administrative_area_level_1");
    },

    isBangkokFromComponents(components, provinceNormalized) {
      const raw = this.pickRawProvince(components);
      return (
        /กรุงเทพ|bangkok/i.test(provinceNormalized || "") ||
        /กรุงเทพ|bangkok/i.test(raw || "")
      );
    },

    /** ตำบล/แขวง สำหรับส่งไปค้นหาในระบบ */
    sanitizeSubDistrictThai(name) {
      if (!name) return "";
      return String(name)
        .replace(/^ตำบล\s+/gi, "")
        .replace(/^แขวง\s*/gi, "")
        .replace(/^ต\.?\s*/gi, "")
        .trim();
    },

    /** อำเภอ/เขต */
    sanitizeDistrictThai(name) {
      if (!name) return "";
      return String(name)
        .replace(/^เขต\s*/gi, "")
        .replace(/^อ\.?\s*/gi, "")
        .replace(/อำเภอ/g, "")
        .trim();
    },

    /**
     * แปลง address_components จาก Geocoder / Places (รวม Place Details) ให้เข้ากับระบบค้นหาไทย
     *
     * กรุงเทพฯ (เช่น แขวงคลองเตย + เขตคลองเตย):
     *   - แขวง/ระดับย่อย: sublocality_level_2 (types: sublocality, sublocality_level_2, political)
     *   - เขต: sublocality_level_1 (types: sublocality, sublocality_level_1, political)
     *
     * ต่างจังหวัด (เช่น ตำบล เขาถ่าน):
     *   - ตำบล: locality (types: locality, political) — อาจมีคำว่า "ตำบล " นำหน้า
     *   - อำเภอ: administrative_area_level_2 (เมื่อไม่ซ้ำกับจังหวัด)
     * plus_code / route ไม่ใช้เป็นขั้นการปกครอง
     */
    parseThaiAddress(placeResult) {
      const empty = {
        province: "",
        district: "",
        subDistrict: "",
        postalCode: "",
        addressDetail: "",
        formattedAddress: "",
      };
      if (!placeResult) return { ...empty };

      const formattedAddress = placeResult.formatted_address || "";
      const components = placeResult.address_components || [];

      const pick = (...types) => this.pickComponent(components, ...types);

      let province = this.pickByType(components, "administrative_area_level_1");
      province = province
        .replace(/^จ\.?\s*/i, "")
        .replace(/จังหวัด/g, "")
        .trim();

      const adm2 = this.pickByType(components, "administrative_area_level_2");
      const locality = this.pickByType(components, "locality");
      const subLv1 = this.pickByType(components, "sublocality_level_1");
      const subLv2 = this.pickByType(components, "sublocality_level_2");
      const postalCode = this.pickByType(components, "postal_code");

      const isBkk = this.isBangkokFromComponents(components, province);

      let district = "";
      let subDistrict = "";

      if (isBkk) {
        if (subLv2) {
          subDistrict = this.sanitizeSubDistrictThai(subLv2);
          district = this.sanitizeDistrictThai(subLv1);
        } else {
          subDistrict = this.sanitizeSubDistrictThai(
            subLv1 || pick("sublocality", "neighborhood")
          );
        }
        if (!district) {
          const dCand = adm2 && adm2 !== province ? adm2 : locality;
          district = this.sanitizeDistrictThai(dCand);
        }
        if (!subDistrict && locality) {
          subDistrict = this.sanitizeSubDistrictThai(locality);
        }
      } else {
        subDistrict = this.sanitizeSubDistrictThai(
          locality || subLv1 || subLv2 || pick("sublocality", "neighborhood")
        );
        const dCand = adm2 && adm2 !== province ? adm2 : "";
        district = this.sanitizeDistrictThai(dCand);
        if (!district && subLv1 && subLv1 !== locality) {
          district = this.sanitizeDistrictThai(subLv1);
        }
      }

      const establishment = this.pickComponent(components, "establishment", "point_of_interest");
      const premise = this.pickComponent(components, "premise");
      const poiLabel = establishment || premise || (placeResult.name && String(placeResult.name).trim()) || "";

      const streetNumber = this.pickComponent(components, "street_number");
      const route = this.pickComponent(components, "route");
      const subpremise = this.pickComponent(components, "subpremise");
      const streetLine = [streetNumber, route, subpremise].filter(Boolean).join(" ").trim();

      const parts = [];
      if (poiLabel) parts.push(poiLabel);
      if (streetLine) parts.push(streetLine);
      let addressDetail = parts.join(" ").trim();
      if (!addressDetail && formattedAddress) {
        addressDetail = formattedAddress.split(",")[0].trim();
      }

      return {
        province,
        district,
        subDistrict,
        postalCode,
        addressDetail,
        formattedAddress,
      };
    },

    mergeGeoAndPlace(geo, placeParsed) {
      if (!geo) return placeParsed;
      if (!placeParsed) return geo;

      const pDetail = (placeParsed.addressDetail || "").trim();
      const gDetail = (geo.addressDetail || "").trim();
      let addressDetail = pDetail;
      if (gDetail) {
        if (!pDetail) addressDetail = gDetail;
        else if (!pDetail.includes(gDetail) && !gDetail.includes(pDetail)) {
          addressDetail = `${pDetail} ${gDetail}`.trim();
        }
      }

      return {
        province: geo.province || placeParsed.province,
        district: geo.district || placeParsed.district,
        subDistrict: geo.subDistrict || placeParsed.subDistrict,
        postalCode: geo.postalCode || placeParsed.postalCode,
        addressDetail,
        formattedAddress: placeParsed.formattedAddress || geo.formattedAddress,
      };
    },

    updatePosition(lat, lng, fromAutocomplete = false, placeFromPicker = null) {
      this.lat = lat;
      this.lng = lng;
      this.marker.setPosition({ lat, lng });
      this.$emit("input", { lat, lng });
      this.$emit("change", { lat, lng });

      if (fromAutocomplete && placeFromPicker) {
        this.fetchGeocode(lat, lng, { mergePlace: placeFromPicker });
        return;
      }
      this.fetchGeocode(lat, lng, { mergePlace: null });
    },

    fetchGeocode(lat, lng, { mergePlace = null } = {}) {
      this.geocoder.geocode(
        { location: { lat, lng }, language: "th", region: "th" },
        (results, status) => {
          const geo = status === "OK" && results[0] ? this.parseThaiAddress(results[0]) : null;
          const placeParsed = mergePlace ? this.parseThaiAddress(mergePlace) : null;
          let out = this.mergeGeoAndPlace(geo, placeParsed);

          if (!out || (!out.province && !out.district && !out.subDistrict && !out.postalCode)) {
            out = geo || placeParsed;
          }

          if (results && results[0] && results[0].formatted_address) {
            this.searchQuery = results[0].formatted_address;
          } else if (mergePlace && mergePlace.formatted_address) {
            this.searchQuery = mergePlace.formatted_address;
          }

          if (out && this.syncAddressFields) {
            this.$emit("address-resolved", out);
          }
        }
      );
    },
    resetToInitial() {
      const initial = this.value || { lat: 13.7563, lng: 100.5018 };
      this.map.setCenter(initial);
      this.updatePosition(initial.lat, initial.lng, false, null);
    },
  },
  watch: {
    value: {
      deep: true,
      handler(v) {
        if (!v) return;
        if (Math.abs(this.lat - v.lat) > 0.000001 || Math.abs(this.lng - v.lng) > 0.000001) {
          this.lat = v.lat;
          this.lng = v.lng;
          if (this.marker) this.marker.setPosition({ lat: v.lat, lng: v.lng });
          if (this.map && !this.isInitialSet) {
            this.map.setCenter({ lat: v.lat, lng: v.lng });
            this.isInitialSet = true;
          }
        }
      },
    },
  },
};
</script>

<style scoped>
.google-map-picker {
  width: 100%;
}

.fill-height-mode {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.fill-height-mode .map-container {
  flex-grow: 1;
  height: 100% !important;
}

.map-container {
  width: 100%;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.coords-bar {
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #f1f5f9;
}
</style>
